import { Controller } from "@hotwired/stimulus"
import Dashboard from '@uppy/dashboard'
import Webcam from '@uppy/webcam'
import { uppyInstance, uploadedFileData } from '../uppy'
import { nanoid } from 'nanoid'
import '@uppy/core/dist/style.css'
import '@uppy/dashboard/dist/style.css'
import '@uppy/webcam/dist/style.css'

export default class extends Controller {
  static targets = ['input']
  static values = {
    types: Array,
    server: String,
    autoSubmit: { type: Boolean, default: true }, // Option pour activer/désactiver la soumission automatique
    fieldName: String, // Nom du champ pour le hidden input (ex: "client[photo]")
    autoUpload: { type: Boolean, default: true } // Option pour activer/désactiver l'upload automatique
  }

  connect() {
    this.uppy = this.createUppy()
    // Count pour suivre les téléchargements
    this.uploadCount = 0
    this.pendingUploads = 0
    
    // Exposer le contrôleur sur l'élément DOM pour permettre l'accès depuis d'autres contrôleurs
    this.element.filesUploadController = this
    
    // Masquer les boutons indésirables après l'initialisation
    this.hideUnwantedButtons()
  }

  // Méthode pour masquer les boutons indésirables via CSS
  hideUnwantedButtons() {
    // // Utiliser un délai pour s'assurer qu'Uppy est complètement initialisé
    // setTimeout(() => {
    //   const style = document.createElement('style')
    //   style.textContent = `
    //     /* Masquer tous les boutons indésirables */
    //     .uppy-DashboardAddFiles-done,
    //     .uppy-DashboardAddFiles-info button,
    //     .uppy-DashboardContent-addMore,
    //     .uppy-Dashboard-AddFiles .uppy-Dashboard-AddFiles-title + button,
    //     .uppy-StatusBar-action,
    //     .uppy-StatusBar-actionBtn,
    //     .uppy-StatusBar-actionBtn--done,
    //     .uppy-Dashboard-AddFiles-info button[type="button"] {
    //       display: none !important;
    //     }
        
    //     /* Masquer spécifiquement le bouton "Done" et "Add more files" */
    //     button:contains("Done"),
    //     button:contains("Add more files"),
    //     .uppy-c-btn--primary:contains("Done") {
    //       display: none !important;
    //     }
        
    //     /* Garder visible seulement le bouton de suppression (croix) */
    //     .uppy-Dashboard-Item-action--remove {
    //       display: block !important;
    //     }
        
    //     /* Masquer la zone d'actions après upload */
    //     .uppy-Dashboard-AddFiles-info {
    //       display: none !important;
    //     }
        
    //     /* Simplifier l'affichage après upload */
    //     .uppy-Dashboard-files {
    //       border: none !important;
    //     }
    //   `
    //   document.head.appendChild(style)
    // }, 100)
  }

  disconnect() {
    // Nettoyer la référence au contrôleur
    if (this.element.filesUploadController === this) {
      delete this.element.filesUploadController
    }
    
    // Nettoyer l'instance Uppy si elle existe
    if (this.uppy) {
      this.uppy.destroy()
    }
  }

  // Méthode pour soumettre le formulaire parent
  submitForm() {
    // Ne soumettre le formulaire que si autoSubmit est activé
    if (!this.autoSubmitValue) {
      return;
    }

    // Trouver le formulaire parent
    const form = this.element.closest('form');
    if (form) {
      form.requestSubmit(); // Utilise requestSubmit pour déclencher les validations
    } else {
      console.warn('No parent form found to submit');
    }
    // Réinitialiser les compteurs
    this.uploadCount = 0;
    this.pendingUploads = 0;
  }

  createUppy() {
    const uppy = uppyInstance({
      id: this.inputTarget.id,
      types: this.typesValue,
      server: this.serverValue,
      restrictions: {
        maxNumberOfFiles: 1, // Limiter à un seul fichier
        minNumberOfFiles: null,
        allowMultipleUploads: false // Empêcher les uploads multiples
      }
    })
      .use(Dashboard, {
        target: this.inputTarget.parentNode,
        inline: true,
        height: 300,
        replaceTargetContent: true,
        autoProceed: this.autoUploadValue, // Upload automatique basé sur la value
        hideUploadButton: this.autoUploadValue, // Cacher le bouton upload si auto
        showProgressDetails: true,
        // closeAfterFinish: true, // SUPPRIMÉ - incompatible avec inline: true
        disableStatusBar: false, // Garder la barre de statut
        hideRetryButton: true, // Cacher le bouton retry
        hideCancelButton: false, // Garder le bouton cancel pour pouvoir supprimer
        hideDoneButtonInAddMorePanel: true, // Cacher le bouton "Terminé"
        hideAddFilesPanel: true, // Cacher complètement le panneau "add more files"
        showAddFilesPanel: false, // Ne pas montrer le panneau "add more files"
        showFileEditor: false, // Pas d'éditeur de fichier
        showRemoveButtonAfterComplete: true, // Montrer le bouton supprimer après upload
        disablePageScrollWhenModalOpen: false,
        proudlyDisplayPoweredByUppy: false, // Enlever la mention "Powered by Uppy"
        doneButtonHandler: null, // Désactiver le handler du bouton "Terminé"
        note: this.autoUploadValue ? 'Sélectionnez une photo (elle sera automatiquement envoyée). Cliquez sur la croix pour la supprimer.' : null
      })
      .use(Webcam, {
        countdown: 3, // Compte à rebours de 3 secondes avant la prise de photo
        mirror: true, // Effet miroir pour la webcam
        facingMode: 'user', // Caméra frontale par défaut
        locale: {
          strings: {
            smile: 'Souriez !',
            takePicture: 'Prendre une photo',
            startRecording: 'Commencer l\'enregistrement',
            stopRecording: 'Arrêter l\'enregistrement',
            allowAccessTitle: 'Veuillez autoriser l\'accès à votre caméra',
            allowAccessDescription: 'Pour prendre des photos avec votre caméra, veuillez autoriser l\'accès à la caméra pour ce site.',
            noCameraTitle: 'Caméra non disponible',
            noCameraDescription: 'Pour prendre des photos, veuillez connecter une caméra',
            recordingStoppedMaxSize: 'L\'enregistrement s\'est arrêté car la taille maximale du fichier a été atteinte',
            recordingLength: 'Durée d\'enregistrement %{recording_length}',
            submitRecordedFile: 'Envoyer la vidéo enregistrée'
          }
        }
      })

    uppy.on('file-added', (file) => {
      // Si on a déjà un fichier, supprimer l'ancien avant d'ajouter le nouveau
      const existingFiles = uppy.getFiles();
      if (existingFiles.length > 1) {
        // Supprimer tous les fichiers sauf le dernier ajouté
        existingFiles.forEach(existingFile => {
          if (existingFile.id !== file.id) {
            uppy.removeFile(existingFile.id);
          }
        });
      }
      
      this.pendingUploads = 1; // Toujours 1 seul fichier
      
      // Si l'upload automatique est activé, démarrer l'upload immédiatement
      if (this.autoUploadValue) {
        setTimeout(() => uppy.upload(), 100); // Petit délai pour laisser l'UI se mettre à jour
      }
    });

    uppy.on('upload', (data) => {
      // Réinitialiser le compteur d'uploads terminés
      this.uploadCount = 0;
    });

    uppy.on('upload-success', (file, response) => {
      // Supprimer les anciens champs cachés avant d'ajouter le nouveau
      const existingHiddenFields = this.element.querySelectorAll(`input[name="${this.fieldNameValue}"]`);
      existingHiddenFields.forEach(field => field.remove());
      
      const hiddenField = document.createElement('input')
      hiddenField.type = 'hidden'
      hiddenField.name = this.fieldNameValue
      hiddenField.value = uploadedFileData(file, response, this.serverValue)

      this.element.appendChild(hiddenField)

      // Avec un seul fichier, on peut directement soumettre
      this.uploadCount = 1;

      // Si tous les fichiers ont été téléchargés, soumettre le formulaire
      if (this.uploadCount === this.pendingUploads) {
        this.submitForm();
      }
    })

    // Gérer la suppression d'un fichier par l'utilisateur
    uppy.on('file-removed', (file) => {
      // Supprimer le champ caché correspondant si il existe
      const hiddenFields = this.element.querySelectorAll(`input[name="${this.fieldNameValue}"]`);
      hiddenFields.forEach(field => field.remove());
      
      // Réinitialiser les compteurs
      this.uploadCount = 0;
      this.pendingUploads = 0;
      
      // Réappliquer le masquage des boutons indésirables
      this.hideUnwantedButtons();
    });

    uppy.on('complete', (result) => {
      // Ne pas appeler uppy.cancelAll() pour garder les fichiers visibles
      // uppy.cancelAll()
      
      // Masquer les boutons indésirables après l'upload
      this.hideUnwantedButtons();
      
      // Vérification additionnelle pour s'assurer que le formulaire est soumis
      if (this.uploadCount === this.pendingUploads && this.pendingUploads > 0) {
        this.submitForm();
      }
    })

    return uppy
  }
}